import styled from '@emotion/styled';
import {Container} from '@nfq/react-grid';
import {observer} from 'mobx-react-lite';
import * as motion from 'motion/react-m';

import {PayloadVideo} from 'UI/components/layout/PayloadVideo';
import {HeroContent} from 'UI/components/module/heroBlock/HeroContent';

import {HeroTransition} from 'UI/animations/hero';

import {generateBgImage} from 'Application/utils/bgImages';
import {isDefinedAndPopulated} from 'Application/utils/typeGuards';

import type {HeroBlockType, Media} from 'types/payload-types';

/**
 * The `ComponentProps` interface defines the expected properties required by the `HeroBlock` component.
 * This interface provides structure and type safety for rendering a hero section with dynamic content and media.
 * It includes configuration for the background (either image or video), the main headline text, and optional linking or testing identifiers.
 * The props are typically derived from a CMS-driven `HeroBlockType` structure to ensure seamless content integration.
 */
interface ComponentProps {
    /**
     * Defines the background of the hero block, which can be either an image or a video.
     * This property determines whether an image is shown statically or a video is rendered dynamically.
     */
    background: HeroBlockType['background'];
    /**
     * The configuration for the hero section's buttons, including the primary CTA and optional secondary button.
     * This determines button visibility, labels, and link behavior.
     */
    buttons: HeroBlockType['buttons'];
    /**
     * The main headline text displayed within the hero section.
     * Rendered as an `<h1>` element, it should communicate the key message of the section.
     */
    headline: HeroBlockType['headline'];
    /**
     * The unique identifier for the hero block, used for linking and accessibility.
     * This ID is essential for in-page navigation and should be unique across the page.
     */
    id: HeroBlockType['id'];
    /**
     * Optional anchor ID used to link the section heading.
     * This can be used for in-page navigation or accessibility purposes.
     */
    linkId: HeroBlockType['linkId'];
    /**
     * The `testId` property represents a unique identifier, usually in the form of a string, assigned to a component for testing purposes.
     * This property is crucial for uniquely identifying components during testing, allowing for more accurate and reliable tests.
     *
     * @default 'HeroBlock'
     */
    testId?: string;
}

/**
 * A layout component that renders the full hero block, including background media and hero content.
 * This component displays a visually rich hero section with optional background images or videos,
 * as well as headline text and CTA buttons. It renders two layers of `HeroContent`: one inside an
 * `Optical` wrapper in a dark theme for visual layering, and another inside the main content area
 * in a light theme for the primary user-facing content.
 * The background media is determined by the `background` prop, which may specify an image or a video.
 * When a video is used, it is rendered via the `BgVideo` component with autoplay, looping, muted, and
 * inline playback enabled. The content area is styled and animated using `HeroTransition` variants.
 * The component is wrapped in MobX's `observer` to reactively update when observable data changes.
 *
 * @param props            The component props.
 * @param props.background The hero background configuration, including type (`'image'` or `'video'`) and the corresponding media source.
 * @param props.buttons    The hero section button configuration, passed to `HeroContent`.
 * @param props.headline   The hero section headline configuration, passed to `HeroContent`.
 * @param props.id         Optional `id` for the inner `HeroContent` headline.
 * @param props.testId     Optional test ID used for automation selectors. Defaults to `'HeroBlock'`.
 * @returns A JSX layout for the hero block with background and layered hero content.
 *
 * @example
 * ```tsx
 * <HeroBlock
 *   background={{ type: 'image', backgroundImage: '/hero.jpg' }}
 *   buttons={heroButtons}
 *   headline={heroHeadline}
 * />
 * ```
 */
const HeroBlock = observer(({background, buttons, headline, id, testId = 'HeroBlock'}: ComponentProps) => (
    <Wrapper>
        <Optical>
            <HeroContent buttons={buttons} headline={headline} testId={testId} theme="dark" isPresentational />
        </Optical>
        <Content
            $bg={background.type === 'image' ? background.backgroundImage : undefined}
            data-cy={testId}
            variants={HeroTransition}
        >
            {background.type === 'video' && (
                <BgVideo
                    className="hero-video"
                    testId={`${testId}-video`}
                    video={background.backgroundVideo}
                    autoPlay
                    loop
                    muted
                    playsInline
                />
            )}
            <Container>
                <HeroContent buttons={buttons} headline={headline} id={id} testId={testId} theme="light" />
            </Container>
        </Content>
    </Wrapper>
));

HeroBlock.displayName = 'HeroBlock';

export {HeroBlock};

const Wrapper = styled.div`
    isolation: isolate;
    max-height: calc(64vh - 0px);
    position: relative;
`;

const Optical = styled(Container)`
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: -1;
`;

interface ContentProps {
    $bg?: Media | string | null | undefined;
}

const Content = styled(motion.div, {shouldForwardProp: prop => !prop.startsWith('$')})<ContentProps>`
    align-items: center;
    background-image: ${({$bg}) => (isDefinedAndPopulated($bg) ? generateBgImage($bg) : null)};
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    clip-path: circle(100% at 50% 100%);
    display: flex;
    height: calc(100vh - 160px);
    isolation: isolate;
    overflow: hidden;
    position: relative;
    width: 100%;

    ${({$bg}) => (isDefinedAndPopulated($bg) && generateBgImage($bg))};
`;

const BgVideo = styled(PayloadVideo)`
    bottom: 0;
    height: 100%;
    left: 0;
    object-fit: cover;
    object-position: center;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
    z-index: -1;
`;